<template>
  <v-navigation-drawer
    id="core-navigation-drawer"
    v-model="drawer"
    :style="drawerStyle"
    :expand-on-hover="expandOnHover"
    color="muted"
    mobile-breakpoint="960"
    app
    mini-variant-width="80"
    width="260"
    v-bind="$attrs"
    overlay-color="#535353"
    overlay-opacity=".9"
  >
    <div
      v-if="!isMultiOrg && !isMobile"
      class="pa-3 text-center na-container clickable"
      @click="$router.push('/')"
    >
      <img
        width="195px"
        src="@/assets/NoiseAware_Logo_HRZ_Cream.png"
        alt="NoiseAware Logo"
      >
    </div>
    <v-list-item
      v-else-if="isMultiOrg && isMobile"
      class="px-0"
    >
      <multi-org-menu attach="#core-navigation-drawer" />
      <v-btn
        fab
        icon
        right
        absolute
        @click="handleDrawerClose"
      >
        <v-icon
          small
          style="color:white"
        >
          $faTimes
        </v-icon>
      </v-btn>
    </v-list-item>
    <v-list-item
      v-else-if="!isMultiOrg && isMobile"
      class="pl-6 py-5 pr-0 d-flex justify-space-between single-org-name"
      style="color:white !important"
    >
      <span>
        {{ currentUser.orgs[0].name }}
      </span>
      <v-btn
        fab
        icon
        right
        absolute
        @click="handleDrawerClose"
      >
        <v-icon
          small
          style="color:white"
        >
          $faTimes
        </v-icon>
      </v-btn>
    </v-list-item>
    <v-list
      class="d-flex flex-column align-center"
      expand
      nav
    >
      <template v-for="(section, id) of menuItems">
        <div
          v-if="!section.requiresPermission || (section.requiresPermission && section.hasPermission)"
          :key="id"
          class="nav-section"
        >
          <v-hover
            v-for="(item, childId) of section.children"
            v-slot="{hover}"
            :key="'c'+childId"
          >
            <div
              v-if="!item.requiresPermission || (item.requiresPermission && item.hasPermission)"
              class="core-nav-item"
              :class="{mobile:isMobile}"
            >
              <div
                class="nav-menu-area-container d-flex justify-center"
                :class="[{ active: $route.path === item.link }, {'on-hover': !($route.path === item.link) && hover}]"
                @click="$router.push(item.link)"
              >
                <div class="nav-title-icon d-flex flex-column justify-center align-center">
                  <v-icon class="nav-icon mb-2">
                    {{ item.icon }}
                  </v-icon>
                  <span class="nav-title">{{ item.name }}</span>
                </div>
                <span
                  v-if="item.showBadge"
                  class="nav-badge"
                >
                  {{ item.badgeContent }}
                </span>
              </div>
            </div>
          </v-hover>
        </div>
      </template>
      <div />
    </v-list>
  </v-navigation-drawer>
</template>

<script>
  import { mapGetters } from 'vuex'
  import MultiOrgMenu from '@/views/components/core/MultiOrgMenu'

  export default {
    name: 'DashboardCoreDrawer',
    components: { MultiOrgMenu },
    props: {
      expandOnHover: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      ...mapGetters({
        currentUser: 'Users/currentUser',
        isMultiOrg: 'Permissions/isMultiOrg',
        currentSelectedOrg: 'Permissions/currentSelectedOrg',
        hasNightAgent: 'Permissions/hasNightAgent',
      }),
      isMobile () {
        return this.$vuetify.breakpoint.mobile
      },
      drawer: {
        get () {
          return this.$store.state.drawer
        },
        set (val) {
          this.$store.commit('setDrawer', val)
        },
      },
      drawerStyle () {
        return this.isMobile ? 'width: 323px' : this.isMultiOrg ? 'margin-left: 74px' : ''
      },
      menuItems () {
        return [
          {
            header: 'Dashboard',
            requiresPermission: false,
            children: [
              {
                name: 'Live Events',
                badgeCount: 0,
                icon: '$faSirenOn',
                link: '/',
              },
              {
                name: 'Events History',
                badgeCount: 0,
                icon: '$faCalendarAlt',
                link: '/events_history',
              },
            ],
          },
          {
            header: 'Manage',
            requiresPermission: false,
            children: [
              {
                name: 'Properties',
                badgeCount: 0,
                icon: '$faBuilding',
                link: '/properties',
              },
              {
                name: 'Integrations',
                requiresPermission: true,
                hasPermission: this.$store.getters['Permissions/canAdminSelectedOrg'],
                badgeCount: 0,
                icon: '$faLink',
                link: `/organizations/${this.currentSelectedOrg.id}/integrations`,
              },
            ],
          },
          {
            header: 'Administrator Tools',
            requiresPermission: true,
            hasPermission: this.$store.getters['Permissions/canAdminSelectedOrg'],
            children: [
              {
                name: 'Organization',
                icon: '$faSitemap',
                link: `/organizations/${this.currentSelectedOrg.id}`,
              },
              {
                name: 'Reservations',
                requiresPermission: true,
                hasPermission: this.$store.getters['Permissions/isInternal'] || this.$store.getters['Permissions/isNightAgent'],
                badgeCount: 0,
                icon: '$faCalendarAlt',
                link: '/reservations',
              },
              {
                name: 'Billing',
                requiresPermission: true,
                hasPermission: this.$store.getters['Billing/currentOrgBillingEnabled'],
                badgeCount: 0,
                badgeContent: '!',
                showBadge: this.$store.getters['Billing/currentOrgHasBillingIssue'],
                icon: '$faMoneyCheckAlt',
                link: '/billing',
              },
            ],
          },
          {
            header: 'Internal',
            requiresPermission: true,
            hasPermission: this.$store.getters['Permissions/isInternal'],
            children: [
              {
                name: 'All Organizations',
                icon: '$faSitemap',
                link: '/organizations',
              },
              {
                name: 'All Users',
                icon: '$faUsers',
                link: '/users',
              },
            ],
          },
        ]
      },
    },
    watch: {
      '$vuetify.breakpoint.smAndDown' (val) {
        this.$emit('update:expandOnHover', !val)
      },
      currentSelectedOrg (newVal, oldVal) {
        if (!oldVal && newVal !== oldVal) {
          this.$store.dispatch('Organizations/fetchOrgDetails', this.currentSelectedOrg.id)
        }
      },
    },
    created () {
      const selectedOrg = this.$store.getters['Organizations/selectedOrg']
      if (selectedOrg) {
        this.$store.dispatch('Organizations/fetchOrgDetails', selectedOrg.id)
      }
    },
    methods: {
      handleDrawerClose () {
        this.drawer = false
      },
    },
  }
</script>
<style lang="scss" scoped>
@mixin white-lato {
  font-family: 'Lato', sans-serif;
  font-weight: 600;
  color: white;
  text-transform: uppercase;
}
#na-logo {
  text-decoration-color: $muted;
}
h6 {
  @include white-lato();
  font-size: 10px;
  line-height: 12px;
  letter-spacing: 1.5px;
}
.nav-section + .nav-section {
  margin-top: 16px;
}
.core-nav-item {
  position: relative;
}
.core-nav-item + .core-nav-item {
  margin-top: 16px;
}
.core-nav-item.mobile {
  .nav-menu-area-container {
    width: 272px;
  }
}
.single-org-name {
  @include white-lato();
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 1px;
  border-bottom: 1px solid #456E6C;
}
.na-container {
  background-color: $muted;
  min-height:78px;
}
.nav-menu-area-container {
  color:white;
  cursor:pointer;
  width:227px;
  height:95px;
  border-radius: 4px;
  background-color: $muted;
  border:2px solid #66857F;
  .nav-title-icon {
    width:80%;
    .nav-icon {
      color:white;
      height:25px;
    }
    .nav-title {
      @include white-lato();
      font-size:13px;
      line-height: 16px;
      letter-spacing: 1px;
    }
  }
  .nav-badge {
    font-size: 13px;
    font-weight: 600;
    font-family: 'Lato', sans-serif;
    line-height: 16px;
    color: #ffffff;
    background-color: $error;
    height: 24px;
    border-radius: 4px;
    padding: 4px 6px;
    position: absolute;
    top: 10px;
    right: 10px;
  }
  &.active {
    background-color: $nav-highlight;
  }
  &.on-hover {
    background-color: $nav-highlight;
    border-color: $nav-highlight;
  }
}
</style>
